import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Divider,
  Container,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Project, SlideshowLang } from '../../types';
import { useLang } from '../../contexts/langContext';
import { usePptxFile } from '../../hooks/usePptxFile';
import { usePptxFiles } from '../../hooks/usePptxFiles';
import PptxFilePickerDialog from './PptxFilePickerDialog';

interface ProjectTabProps {
  project: Project;
  onEdit: (updatedProject: Partial<Project>) => void;
  onDelete: () => void;
  onPptxFileChange?: (fileId: string) => void;
}

interface EditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  title: string;
  value: string;
  multiline?: boolean;
}

function EditDialog({ open, onClose, onSave, title, value, multiline }: EditDialogProps) {
  const [editValue, setEditValue] = useState(value);
  const { lang } = useLang();

  const handleSave = () => {
    onSave(editValue);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          multiline={multiline}
          rows={multiline ? 4 : 1}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
        <Button onClick={handleSave} variant="contained">
          {lang === 'en' ? 'Save' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface EditSlidesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (value: number) => void;
  currentValue: number;
}

function EditSlidesDialog({ open, onClose, onSave, currentValue }: EditSlidesDialogProps) {
  const [numSlides, setNumSlides] = useState(currentValue);
  const { lang } = useLang();

  const handleSave = () => {
    onSave(numSlides);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{lang === 'en' ? 'Edit Number of Slides' : '编辑幻灯片数量'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          type="number"
          value={numSlides}
          onChange={(e) => setNumSlides(parseInt(e.target.value) || 0)}
          inputProps={{ min: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
        <Button onClick={handleSave} variant="contained">
          {lang === 'en' ? 'Save' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface EditLangDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  currentValue: string;
}

function EditLangDialog({ open, onClose, onSave, currentValue }: EditLangDialogProps) {
  const [selectedLang, setSelectedLang] = useState(currentValue);
  const { lang } = useLang();

  const handleSave = () => {
    onSave(selectedLang);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{lang === 'en' ? 'Select Language' : '选择语言'}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="dense">
          <InputLabel>{lang === 'en' ? 'Language' : '语言'}</InputLabel>
          <Select value={selectedLang} onChange={(e) => setSelectedLang(e.target.value)} label={lang === 'en' ? 'Language' : '语言'} autoFocus>
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="zh-CN">简体中文</MenuItem>
            <MenuItem value="zh-HK">繁體中文</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
        <Button onClick={handleSave} variant="contained">
          {lang === 'en' ? 'Save' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ProjectTab({ project, onEdit, onDelete, onPptxFileChange }: ProjectTabProps) {
  const { lang } = useLang();
  const { pptxFile } = usePptxFile(project.pptxFileId);
  const { pptxFiles } = usePptxFiles();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [editTitleDialogOpen, setEditTitleDialogOpen] = useState(false);
  const [editDescriptionDialogOpen, setEditDescriptionDialogOpen] = useState(false);
  const [editSlidesDialogOpen, setEditSlidesDialogOpen] = useState(false);
  const [editLangDialogOpen, setEditLangDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isTemplateWarningOpen, setIsTemplateWarningOpen] = useState(false);
  const [pendingPptxFileId, setPendingPptxFileId] = useState<string | null>(null);

  const pendingPptxFile = pendingPptxFileId ? pptxFiles.find((file) => file._id === pendingPptxFileId) : undefined;

  const handleProjectUpdate = (update: Partial<Project>) => {
    onEdit(update);
  };

  const handlePptxFileSelect = (fileId: string) => {
    setIsPickerOpen(false);
    if (fileId === project.pptxFileId) {
      return;
    }
    setPendingPptxFileId(fileId);
    setIsTemplateWarningOpen(true);
  };

  const handleTemplateChangeCancel = () => {
    setPendingPptxFileId(null);
    setIsTemplateWarningOpen(false);
  };

  const handleTemplateChangeConfirm = () => {
    if (!pendingPptxFileId) {
      return;
    }
    if (onPptxFileChange) {
      onPptxFileChange(pendingPptxFileId);
    }
    handleProjectUpdate({ pptxFileId: pendingPptxFileId, slides: [] });
    setPendingPptxFileId(null);
    setIsTemplateWarningOpen(false);
  };

  const isProjectNameMatch = (input: string) => {
    return input.toLowerCase() === project.title.toLowerCase();
  };

  const handleDeleteClick = () => {
    if (isProjectNameMatch(deleteConfirmation)) {
      setIsDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    setIsDeleteDialogOpen(false);
    setDeleteConfirmation('');
    onDelete();
  };

  return (
    <Container maxWidth="sm">
      <List>
        <ListItemButton onClick={() => setEditTitleDialogOpen(true)}>
          <ListItemText primary={lang === 'en' ? 'Title' : '标题'} secondary={project.title} />
        </ListItemButton>
        <Divider />

        <ListItemButton onClick={() => setEditDescriptionDialogOpen(true)}>
          <ListItemText primary={lang === 'en' ? 'Description' : '描述'} secondary={project.description} />
        </ListItemButton>
        <Divider />

        <ListItemButton onClick={() => setEditLangDialogOpen(true)}>
          <ListItemText
            primary={lang === 'en' ? 'Language' : '语言'}
            secondary={
              project.lang === 'en' ? 'English' : project.lang === 'zh-CN' ? '简体中文' : project.lang === 'zh-HK' ? '繁體中文' : project.lang
            }
          />
        </ListItemButton>
        <Divider />

        <ListItemButton onClick={() => setEditSlidesDialogOpen(true)}>
          <ListItemText primary={lang === 'en' ? 'Number of Slides' : '幻灯片数量'} secondary={project.numSlides} />
        </ListItemButton>
        <Divider />

        <ListItemButton onClick={() => setIsPickerOpen(true)}>
          <ListItemText primary={lang === 'en' ? 'PPTX Template' : 'PPTX 模板'} secondary={pptxFile ? pptxFile.filename : 'No PPTX file'} />
        </ListItemButton>
      </List>

      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h6" color="error" gutterBottom>
          {lang === 'en' ? 'Danger Zone' : '危险区域'}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {lang === 'en' ? 'To delete this project, please type the project name to confirm:' : '要删除此项目，请输入项目名称以确认：'}
          <strong>{project.title}</strong>
        </Typography>
        <Stack spacing={2}>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            error={deleteConfirmation !== '' && !isProjectNameMatch(deleteConfirmation)}
            helperText={
              deleteConfirmation !== '' && !isProjectNameMatch(deleteConfirmation)
                ? lang === 'en'
                  ? 'Project name does not match'
                  : '项目名称不匹配'
                : ''
            }
          />
          <Button
            startIcon={<DeleteIcon />}
            variant="outlined"
            color="error"
            onClick={handleDeleteClick}
            disabled={!isProjectNameMatch(deleteConfirmation)}
          >
            {lang === 'en' ? 'Delete Project' : '删除项目'}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {lang === 'en' ? 'Created: ' : '创建时间：'}
          {new Date(project.createdAt).toLocaleString()}
          <br />
          {lang === 'en' ? 'Last Updated: ' : '更新时间：'}
          {new Date(project.updatedAt).toLocaleString()}
        </Typography>
      </Box>

      <EditDialog
        open={editTitleDialogOpen}
        onClose={() => setEditTitleDialogOpen(false)}
        onSave={(value) => handleProjectUpdate({ title: value })}
        title={lang === 'en' ? 'Edit Title' : '编辑标题'}
        value={project.title}
      />

      <EditDialog
        open={editDescriptionDialogOpen}
        onClose={() => setEditDescriptionDialogOpen(false)}
        onSave={(value) => handleProjectUpdate({ description: value })}
        title={lang === 'en' ? 'Edit Description' : '编辑描述'}
        value={project.description}
        multiline
      />

      <EditSlidesDialog
        open={editSlidesDialogOpen}
        onClose={() => setEditSlidesDialogOpen(false)}
        onSave={(value) => {
          const currentSlides = [...project.slides];
          if (value > currentSlides.length) {
            const newSlides = Array(value - currentSlides.length).fill({});
            handleProjectUpdate({ slides: [...currentSlides, ...newSlides], numSlides: value });
          } else {
            handleProjectUpdate({ slides: currentSlides.slice(0, value), numSlides: value });
          }
        }}
        currentValue={project.numSlides}
      />

      <EditLangDialog
        open={editLangDialogOpen}
        onClose={() => setEditLangDialogOpen(false)}
        onSave={(value) => handleProjectUpdate({ lang: value as SlideshowLang })}
        currentValue={project.lang}
      />

      <PptxFilePickerDialog
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={handlePptxFileSelect}
        pptxFiles={pptxFiles}
        currentFileId={project.pptxFileId}
      />

      <Dialog open={isTemplateWarningOpen} onClose={handleTemplateChangeCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'warning.main' }}>{lang === 'en' ? 'Change Template?' : '更换模板？'}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {lang === 'en'
              ? 'Changing the PPTX template will reset all slides. This action cannot be undone.'
              : '更换 PPTX 模板将重置所有幻灯片，此操作无法撤销。'}
          </Typography>
          {pendingPptxFile && (
            <Typography variant="body2" color="text.secondary">
              {lang === 'en' ? 'New template: ' : '新模板：'}
              {pendingPptxFile.filename}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTemplateChangeCancel}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
          <Button onClick={handleTemplateChangeConfirm} variant="contained" color="warning">
            {lang === 'en' ? 'Switch Template' : '更换模板'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle color="error">{lang === 'en' ? 'Confirm Delete' : '确认删除'}</DialogTitle>
        <DialogContent>
          <Typography>
            {lang === 'en'
              ? 'Are you sure you want to delete this project? This action cannot be undone.'
              : '你确定要删除这个项目吗？此操作无法撤销。'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {lang === 'en' ? 'Delete' : '删除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
